import React, { useMemo, useState } from 'react';

/**
 * Server-driven select form rendered from `response.control`.
 *
 * Two payload shapes are observed in the wild and both normalize to the same
 * internal `sections` model, so one component covers every screen:
 *
 *   flat        { type, options, submit_label }
 *   sectioned   { sections: [{ title, label, type, options }], submit_label }
 *   fielded     { fields: [{ key, label, prompt, type, options }], submit_label }
 *
 * Modelled on ExperienceLevelForm (same radio styling, same "reveal the submit
 * button once every question is answered" behaviour), but with nothing hardcoded.
 */

export interface AssistantControlOption {
  label: string;
  value: string;
}

export type AssistantControlType = 'single_select' | 'multi_select';

interface RawControlSection {
  id?: string;
  key?: string;
  title?: string;
  label?: string;
  prompt?: string;
  help?: string;
  description?: string;
  type?: AssistantControlType;
  options?: AssistantControlOption[];
}

export interface RawAssistantControl extends RawControlSection {
  sections?: RawControlSection[];
  fields?: RawControlSection[];
  submit_label?: string;
}

export interface NormalizedControlSection {
  id: string;
  title?: string;
  label?: string;
  type: AssistantControlType;
  options: AssistantControlOption[];
}

interface AssistantControlFormProps {
  control: RawAssistantControl;
  /** Locks the form — set once a later message exists, or on history replay. */
  isSubmitted?: boolean;
  /** Previously chosen values, keyed by section id, for history replay. */
  initialValues?: Record<string, string[]>;
  /** `input` is what goes on the wire; `labels` is what goes in the transcript. */
  onSubmit: (input: string, labels: string[]) => void;
}

const hasItems = (value?: RawControlSection[]) => Array.isArray(value) && value.length > 0;

const normalizeControl = (control: RawAssistantControl) => {
  // `sections` and `fields` are the same list under two names; a control with
  // neither is the flat shape and stands in as its own single section.
  const rawSections: RawControlSection[] = hasItems(control?.sections)
    ? control.sections!
    : hasItems(control?.fields)
      ? control.fields!
      : [control];

  const sections: NormalizedControlSection[] = rawSections
    .filter((section) => Array.isArray(section?.options) && section.options.length > 0)
    .map((section, index) => {
      // A `fields` entry carries its heading in `label` and its question in
      // `prompt`; a `sections` entry carries the heading in `title` and the
      // hint in `label`. `prompt` is what tells the two apart.
      const isField = Boolean(section.prompt);
      return {
        id: section.key || section.id || section.title || section.label || `section-${index}`,
        title: isField ? section.title || section.label : section.title,
        label: isField
          ? section.prompt
          : section.label || section.help || section.description,
        type: (section.type === 'multi_select'
          ? 'multi_select'
          : 'single_select') as AssistantControlType,
        options: section.options!,
      };
    });

  return { sections, submitLabel: control?.submit_label || 'Done' };
};

/** All choices carried by a control, including choices nested in sections. */
const getControlOptions = (control?: RawAssistantControl | null): AssistantControlOption[] =>
  control ? normalizeControl(control).sections.flatMap((section) => section.options) : [];

/** Values as persisted by the assistant (`a,b,c`) or supplied by a fixture. */
const parseControlAnswer = (answer: unknown): string[] => {
  const values = Array.isArray(answer)
    ? answer
    : typeof answer === 'string'
      ? answer.split(',')
      : [];

  return values
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.trim())
    .filter(Boolean);
};

/** Re-check persisted selections in their original control sections. */
const getControlInitialValues = (
  control: RawAssistantControl,
  answer: unknown,
): Record<string, string[]> => {
  const answered = new Set(parseControlAnswer(answer));

  return normalizeControl(control).sections.reduce(
    (values: Record<string, string[]>, section) => {
      const selected = section.options
        .map((option) => option.value)
        .filter((value) => answered.has(value));
      if (selected.length > 0) values[section.id] = selected;
      return values;
    },
    {},
  );
};

const AssistantControlForm: React.FC<AssistantControlFormProps> = ({
  control,
  isSubmitted = false,
  initialValues,
  onSubmit,
}) => {
  const { sections, submitLabel } = useMemo(() => normalizeControl(control), [control]);
  const [values, setValues] = useState<Record<string, string[]>>(initialValues || {});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const locked = isSubmitted || hasSubmitted;

  const toggle = (section: NormalizedControlSection, value: string) => {
    if (locked) return;
    setValues((prev) => {
      const current = prev[section.id] || [];
      if (section.type === 'single_select') {
        return { ...prev, [section.id]: [value] };
      }
      return {
        ...prev,
        [section.id]: current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });
  };

  const isComplete =
    sections.length > 0 && sections.every((section) => (values[section.id] || []).length > 0);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isComplete || locked) return;
    // The backend reads a single `input` string. A single-select answer is sent as
    // the bare option value (matching what conversation history records); anything
    // with multiple selections is comma-joined.
    const selected = sections.flatMap((section) =>
      (values[section.id] || []).map((value) => ({
        value,
        label: section.options.find((option) => option.value === value)?.label ?? value,
      }))
    );
    setHasSubmitted(true);
    onSubmit(
      selected.map((s) => s.value).join(','),
      selected.map((s) => s.label)
    );
  };

  if (sections.length === 0) return null;

  return (
    <div className={`w-full pl-[50px] bg-transparent rounded-lg ${locked ? 'pointer-events-none' : ''}`}>
      {/* Hairline rules frame the control block, matching the design. */}
      <form onSubmit={handleSubmit} className="space-y-6 border-y border-gray-200 py-5">
        {sections.map((section, index) => (
          <div
            key={section.id}
            className={`space-y-3 ${index > 0 ? 'pt-4 border-t border-gray-200' : ''}`}
          >
            {section.title && (
              <h3 className="text-base font-semibold text-gray-900">{section.title}</h3>
            )}
            {section.label && <p className="text-sm text-gray-600">{section.label}</p>}
            <div className="space-y-2">
              {section.options.map((option) => {
                const selected = (values[section.id] || []).includes(option.value);
                return (
                  <label
                    key={option.value}
                    className="flex items-center space-x-3 cursor-pointer group"
                  >
                    <input
                      type={section.type === 'multi_select' ? 'checkbox' : 'radio'}
                      name={section.id}
                      value={option.value}
                      checked={selected}
                      onChange={() => toggle(section, option.value)}
                      disabled={locked}
                      className={`w-4 h-4 border-gray-300 cursor-pointer accent-[#012169]
                        disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150
                        ${section.type === 'multi_select' ? 'rounded' : 'rounded-full'}`}
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">
                      {option.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}

        {!locked && isComplete && (
          <div className="pt-4 flex justify-end">
            <button
              type="submit"
              className="
                mt-[20px] font-normal bg-[#012169] border border-[#012169] rounded-[10px]
                px-6 py-2 text-white cursor-pointer flex items-start w-fit shrink-0 h-full
                transition-all duration-200 ease-in-out
                hover:-translate-y-[2px] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]
              "
            >
              {submitLabel}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export {
  AssistantControlForm,
  getControlInitialValues,
  getControlOptions,
  normalizeControl,
  parseControlAnswer,
};
